<template>
    <div class="max-w-full flex-none flex items-center pt-8 mb-8">
        <div class="flex items-center justify-center mr-16 rounded bg-primary-5 flex-none drop-shadow overflow-hidden">
            <img v-if="type === 'image' && imagePreview" :src="imagePreview" class="object-cover w-40 h-40" :alt="file.name">
            <div v-if="type === 'document'" class="p-12">
                <svg-icon type="description" class="w-16 h-16"></svg-icon>
            </div>
        </div>
        <div class="overflow-hidden flex-1 relative flex items-center justify-between" ref="container">
            <div class="grow">
                <div class="flex items-center mb-8" ref="header">
                    <div class="flex-initial truncate font-semibold mr-4 whitespace-nowrap" ref="fileName">
                        {{ file.name }}
                    </div>
                </div>
                <div class="flex items-center gap-8 w-full">
                    <div class="h-4 bg-primary-20 rounded-32 grow" ref="progress">
                        <div ref="progressBar" class="progress-bar w-full h-full rounded-32"
                             :class="[error ? 'bg-semantic-error' : 'bg-secondary']"></div>
                    </div>
                    <div class="flex items-end" ref="uploadDetail">
                        <span class="flex-none font-semibold mr-16">{{ Math.round(animationProgress) }}%</span>
                    </div>
                </div>
            </div>
            <div class="flex items-center flex-nowrap gap-8 absolute end-0 opacity-0" ref="icons">
                <button role="button" @click="downloadFile">
                    <svg-icon class="w-18 h-18" type="download"/>
                </button>
                <button role="button" class="hover:text-semantic-error" @click="cancelFile">
                    <svg-icon class="w-18 h-18" type="delete" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import * as files from '../../services/FileService';
import anime from 'animejs';
import SvgIcon from '../ui/SvgIcon.vue';
import axios from 'axios';

export default {
    components: {
        'svg-icon': SvgIcon,
    },
    props: {
        file: {
            required: true,
            type: File,
        },
        dontUpload: Boolean
    },
    data: function () {
        return {
            type: 'document',
            animation: null,
            uploading: false,
            animationProgress: 0,
            imagePreview: null,
            queuedFile: null,
            source: null,
            error: null,
        }
    },
    computed: {
        fileSize() {
            return (this.file.size / 1000000).toFixed(2) + ' MB';
        }
    },

    methods: {
        startUpload() {
            let data = new FormData();

            data.append('file', this.file);
            data.append('type', this.type);

            this.source = axios.CancelToken.source();
            this.uploading = true;

            files.upload(data, progress => this.animateProgress(progress / 100), this.source)
                .then(result => {
                    this.queuedFile = result;
                    this.uploading = false;
                    this.$emit('success', result);
                })
                .catch(error => {
                    this.error = error;
                    this.uploading = false;
                })
                .finally(() => {
                    this.animateProgress(1);
                });
        },
        animateProgress(to) {

            let from = this.animationProgress / 100;

            this.animation = anime({
                targets: this.$refs.progressBar,
                scaleX: [from, to],
                duration: 500,
                easing: 'linear',
                update: anim => {
                    this.animationProgress = ((to - from) * anim.progress) + (from * 100);
                },
                complete: anim => {
                    if (to === 1 && !this.uploading) {
                        this.animateFinished();
                    }
                }
            });
        },
        animateFinished() {
            try {
                let move = ((this.$refs.container.offsetHeight - this.$refs.header.offsetHeight) / 2) - 2;

                anime.timeline({
                    easing: 'easeInOutQuad',
                })
                    .add({
                        targets: [this.$refs.progress, this.$refs.uploadDetail],
                        opacity: 0,
                        duration: 400,
                    })
                    .add({
                        targets: this.$refs.fileName,
                        translateY: move,
                        duration: 500,
                    }, 300)
                    .add({
                        targets: this.$refs.icons,
                        opacity: [0, 1],
                        translateX: [30, 0],
                        duration: 500,
                    }, 300)
            } catch {

            }
        },
        getImagePreview() {
            let reader = new FileReader();
            reader.onload = e => { this.imagePreview = e.target.result; };
            reader.readAsDataURL(this.file)
        },
        downloadFile() {
            let url = URL.createObjectURL(this.file);
            let a = document.createElement('a');
            a.href = url;
            a.download = this.file.name;
            a.click();
            URL.revokeObjectURL(url);
            a.remove();
        },
        cancelFile() {
            if (this.uploading) {
                this.source.cancel(this.$t('general.uploads.canceled_by_user'));
            }

            this.$emit('cancel', this.queuedFile);
        },

        // This function is used externally
        validFile() {
            return !!this.queuedFile;
        }
    },
    created() {
        if (this.file.type.match(/image\/.+/)) {
            this.type = 'image';
        } else {
            this.type = 'document';
        }
    },
    mounted() {
        if (this.dontUpload) {
            this.animateFinished();
        } else {
            this.startUpload();
        }

        if (this.type === 'image') {
            this.getImagePreview();
        }
    }
}
</script>

<style scoped>
.progress-bar {
    transform-origin: left center;
    transform: scaleX(0);
}
</style>
